import moment from 'moment';
import convert from 'convert-units';
import uuidv1 from 'uuid/v1';

import { plannerComponentTypesEnum } from './enums';
import ActivityElement from './ActivityElement';
import RepeaterElement from './RepeaterElement';
import TimeConversionUtils from '../timeConversionUtils';
import { sum } from '../functions';
import store from '../../store/store';

const inflection = require('inflection');

export const REST_ACTIVITY_TYPE_ID = 34;

export default class Workout {
  setProps() {
    this.trainingPlanId = uuidv1();
    this.localStartTime = moment();
    this.activityType = null;
    this._activityTypeId = 2;
    this.load = 0;
    this.planDefinition = [];
    this.title = null;
    this.totalDistance = 1;
    this.totalDuration = 1;
    this.removedOn = null;

    /* Do we need this? */
    this.repsCount = 0;
    this.repsDuration = 0;
    this.repsZone = 0;
    this.dailyDiaryId = null;
  }

  constructor(trainingPlan = null) {
    this.setProps();

    if (trainingPlan) {
      const camelCaseDefaults = {};
      Object.keys(trainingPlan)
        .forEach(
          (key) => {
            camelCaseDefaults[inflection.camelize(key, true)] = trainingPlan[key];
          },
        );
      if (camelCaseDefaults.localStartTime && typeof camelCaseDefaults.localStartTime === 'string') {
        camelCaseDefaults.localStartTime = moment(camelCaseDefaults.localStartTime);
      }
      if (camelCaseDefaults.trainingPlanDate) {
        camelCaseDefaults.localStartTime = moment(camelCaseDefaults.trainingPlanDate);
        delete camelCaseDefaults.trainingPlanDate;
      }
      Object.assign(this, camelCaseDefaults);
    }

    if (trainingPlan && trainingPlan.planDefinition) {
      this.planDefinition = [];
      const elements = trainingPlan.planDefinition.map((element) => {
        let richElement = null;
        switch (element.type) {
          case plannerComponentTypesEnum.activity:
            richElement = new ActivityElement(element);
            break;
          case plannerComponentTypesEnum.repeater:
            richElement = new RepeaterElement(element);
            break;
          default:
            throw Error(`Unknown planner component type: ${element.type}`);
        }
        return richElement;
      });
      this.planDefinition = this.planDefinition.concat(elements);
    }
  }

  get durationForDisplay() {
    return TimeConversionUtils.prettyPrintFullDurationHoursMinutesSeconds(
      moment.duration(this.getEstimatedDuration(), 'seconds'),
    );
  }

  distanceForDisplay(units) {
    return `${convert(this.getEstimatedDistance()).from('mi').to(units || 'mi').toFixed(2)} ${units}`;
  }

  get id() {
    return this.trainingPlanId;
  }

  get activityTypeId() {
    return this._activityTypeId;
  }

  set activityTypeId(newActivityTypeId) {
    // If the new activity type a rest activity then
    // clear out the planDefinition
    if (Workout.getActivityTypeIdIsRestType(newActivityTypeId)) {
      this.planDefinition = [];
    }

    // If the old activity type was a rest activity
    // and the new one is not them add an activity to
    // planDefinition
    if (this.isRestActivityType()
      && !Workout.getActivityTypeIdIsRestType(newActivityTypeId)) {
      this.planDefinition.push(new ActivityElement());
    }

    this._activityTypeId = newActivityTypeId;
  }

  isRestActivityType() {
    return this.activityTypeId
      && Workout.getActivityTypeIdIsRestType(this.activityTypeId);
  }

  // @param plannerComponents is the list of training planner components
  // making up a training plan
  getEstimatedLoad() {
    const calcs = this.planDefinition.reduce((a, c) => {
      if (!c.estimatedDistance || !c.estimatedDuration) {
        return a;
      }
      return {
        duration: a.duration + c.estimatedDuration,
        power: a.power + store.getters.averagePower(c.zone),
        count: a.count + ((c.estimatedDistance > 0 || c.estimatedDuration > 0) ? 1 : 0),
      };
    }, { duration: 0, power: 0, count: 0 });

    calcs.power /= calcs.count;
    const cp = store.getters.criticalPowerNow;
    return calcs.duration * (calcs.power / cp);
  }

  // deprecated
  // @param plannerComponents is the list of training planner components
  // making up a training plan
  getEstimatedDistance() {
    return parseFloat(Workout.getEstimatedDistanceForTrainingPlanElements(this.planDefinition));
  }

  get estimatedDistance() {
    return this.getEstimatedDistance();
  }

  // @param plannerComponents is the list of training planner components
  // making up a training plan
  getEstimatedDuration() {
    return Workout.getEstimatedDurationForTrainingPlanElements(this.planDefinition);
  }

  static getEstimatedDistanceForTrainingPlanElements(plannerElements) {
    return plannerElements.map(e => (parseFloat(e.estimatedDistance || 0))).reduce(sum, 0);
  }

  static getEstimatedDurationForTrainingPlanElements(plannerElements) {
    return plannerElements.map(e => e.estimatedDuration).reduce(sum, 0);
  }

  // @param activityTypeId is an activity type id
  static getActivityTypeIdIsRestType(activityTypeId) {
    return activityTypeId === REST_ACTIVITY_TYPE_ID;
  }
}
